<script setup>
import { reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { LogIn } from '@lucide/vue'
import { Button, Card, FormField, Input } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useAdminAuth } from '../composables/useAdminAuth.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAdminAuth()
const form = reactive({ email: '', password: '' })
const error = ref('')

async function submit() {
  error.value = ''
  try {
    await auth.login(form)
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/waitlist'
    router.replace(redirect)
  } catch {
    error.value = t('visualGameAdmin.auth.failed')
  }
}
</script>

<template>
  <main class="login-page" data-insp-path="apps/visual-game-admin/src/pages/Login.vue#form">
    <Card class="login-card">
      <div class="login-card__brand">
        <span><LogIn :size="22" /></span>
        <h1>{{ t('visualGameAdmin.auth.title') }}</h1>
        <p>{{ t('visualGameAdmin.auth.subtitle') }}</p>
      </div>
      <form class="login-card__form" @submit.prevent="submit">
        <FormField :label="t('visualGameAdmin.auth.email')">
          <Input v-model="form.email" autocomplete="username" type="email" required />
        </FormField>
        <FormField :label="t('visualGameAdmin.auth.password')">
          <Input v-model="form.password" autocomplete="current-password" type="password" required />
        </FormField>
        <p v-if="error" class="vga-error">{{ error }}</p>
        <Button variant="primary" :loading="auth.loading.value" @click="submit">
          {{ auth.loading.value ? t('visualGameAdmin.auth.loggingIn') : t('visualGameAdmin.auth.login') }}
        </Button>
      </form>
    </Card>
  </main>
</template>

<style scoped>
.login-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: var(--vgs-space-3xl);
  background: var(--drama-color-bg);
}

.login-card {
  width: min(480px, 100%);
}

.login-card__brand {
  display: grid;
  gap: var(--drama-space-md);
  margin-bottom: var(--drama-space-xl);
}

.login-card__brand span {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border: 1px solid var(--vgs-line-gold);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-primary);
}

.login-card__brand h1,
.login-card__brand p {
  margin: 0;
}

.login-card__brand p {
  color: var(--drama-color-text-muted);
  line-height: 1.5;
}

.login-card__form {
  display: grid;
  gap: var(--drama-space-lg);
}
</style>
